import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useRecaptchaAction } from "../lib/useRecaptchaAction";
import { RecaptchaDisclosure } from "../components/RecaptchaDisclosure";
import { ChevronDown, ChevronRight, Send, CheckCircle2, ShieldCheck } from "lucide-react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GROUP_LABELS = {
    fib: "Felicidade Interna Bruta (FIB)",
    whr: "World Happiness Report (WHR)",
    global: "Geral",
    custom: "Personalizadas",
};

export default function PublicSurveyPage() {
    const { token } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const [responses, setResponses] = useState({});
    const [open, setOpen] = useState({ fib: true, whr: true, global: true, custom: true });
    const [busy, setBusy] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const { getToken } = useRecaptchaAction();

    useEffect(() => {
        let mounted = true;
        axios.get(`${API_BASE}/public/survey/${token}`)
            .then((r) => {
                if (!mounted) return;
                // Server-side deadline check: if closed, jump straight to the
                // "Inquérito encerrado" page without rendering the form.
                if (r.data?.closed) {
                    navigate(`/s/${token}/closed?closed_at=${encodeURIComponent(r.data.closes_at || "")}`, { replace: true });
                    return;
                }
                setData(r.data);
                setLoading(false);
            })
            .catch((err) => {
                if (!mounted) return;
                const msg = err.response?.data?.detail || err.message || "Erro ao carregar o inquérito";
                setError(msg);
                setLoading(false);
            });
        return () => { mounted = false; };
    }, [token, navigate]);

    const grouped = useMemo(() => {
        const g = {};
        for (const q of data?.questions || []) {
            (g[q.group || "global"] ||= []).push(q);
        }
        for (const k of Object.keys(g)) g[k].sort((a, b) => a.order - b.order);
        return g;
    }, [data]);

    const allMandatory = useMemo(
        () => (data?.questions || []).filter((q) => q.mandatory).map((q) => q.id),
        [data]
    );
    const answered = useMemo(
        () => allMandatory.filter((id) => responses[id] !== undefined && responses[id] !== "" && !(Array.isArray(responses[id]) && responses[id].length === 0)),
        [allMandatory, responses]
    );
    const progress = allMandatory.length === 0 ? 100 : Math.round((answered.length / allMandatory.length) * 100);

    const setVal = (qid, v) => setResponses({ ...responses, [qid]: v });
    const toggleMulti = (qid, opt) => {
        const cur = Array.isArray(responses[qid]) ? responses[qid] : [];
        setVal(qid, cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt]);
    };

    const submit = async () => {
        const missing = allMandatory.filter((id) => !(responses[id] !== undefined && responses[id] !== ""));
        if (missing.length) {
            toast.error(`Faltam ${missing.length} resposta(s) obrigatória(s)`);
            const firstQ = (data?.questions || []).find((q) => q.id === missing[0]);
            if (firstQ) {
                const el = document.querySelector(`[data-testid="pq-${firstQ.id}"]`);
                if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
            }
            return;
        }
        setBusy(true);
        try {
            const recaptcha_token = await getToken("public_survey");
            const payload = {
                recaptcha_token,
                responses: Object.entries(responses)
                    .filter(([, v]) => v !== undefined && v !== "")
                    .map(([question_id, value]) => ({ question_id, value })),
            };
            await axios.post(`${API_BASE}/public/survey/${token}/responses`, payload);
            setSubmitted(true);
        } catch (err) {
            // Deadline expired during fill-in: server responds 410 survey_closed.
            if (err?.response?.status === 410 && err.response.data?.detail?.code === "survey_closed") {
                const closedAtIso = err.response.data.detail.closed_at || "";
                navigate(`/s/${token}/closed?closed_at=${encodeURIComponent(closedAtIso)}`, { replace: true });
                return;
            }
            const detail = err.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : (detail?.message || err.message || "Erro ao submeter"));
        } finally { setBusy(false); }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#fdfcf9] flex items-center justify-center" data-testid="public-survey-loading">
                <span className="label-caps text-[#52525b]">A carregar inquérito...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-[#fdfcf9] flex items-center justify-center px-4" data-testid="public-survey-error">
                <div className="max-w-md text-center border border-[#e4e4e7] p-10 bg-white">
                    <div className="label-caps text-[#b89b6f]">Link indisponível</div>
                    <h1 className="font-display text-3xl text-[#0d1628] mt-3">Não foi possível abrir o inquérito</h1>
                    <p className="text-[#52525b] mt-4 text-sm">{error}</p>
                    <Link to="/" className="btn-outline mt-6 inline-flex">Voltar ao início</Link>
                </div>
            </div>
        );
    }

    if (submitted) {
        return (
            <div className="min-h-screen bg-[#fdfcf9] flex items-center justify-center px-4" data-testid="public-survey-success">
                <div className="max-w-md text-center border border-[#e4e4e7] p-10 bg-white">
                    <CheckCircle2 size={48} className="text-[#10b981] mx-auto" />
                    <h1 className="font-display text-3xl text-[#0d1628] mt-4">Obrigado!</h1>
                    <p className="text-[#52525b] mt-3 text-sm">As suas respostas foram registadas de forma totalmente anónima.</p>
                    <p className="text-[#52525b] mt-2 text-xs">{data?.company?.name}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#fdfcf9] py-10 px-4" data-testid="public-survey-page">
            <div className="max-w-3xl mx-auto">
                <div className="border border-[#e4e4e7] bg-white p-6 mb-6 flex items-center gap-4">
                    {data?.company?.logo_path && (
                        <img
                            src={`${API_BASE}/files/${data.company.logo_path}`}
                            alt={data.company.name}
                            className="h-12 max-w-[120px] object-contain"
                        />
                    )}
                    <div className="flex-1">
                        <div className="label-caps text-[#b89b6f]">{data?.company?.name || "Empresa"}</div>
                        <h1 className="font-display text-3xl text-[#0d1628] mt-1">
                            Inquérito · {data?.project?.name}
                        </h1>
                        <p className="text-xs text-[#52525b] mt-2 flex items-center gap-2">
                            <ShieldCheck size={12} className="text-[#10b981]" />
                            As suas respostas são <strong>anónimas</strong> e usadas apenas para fins estatísticos.
                        </p>
                    </div>
                </div>

                <div className="border border-[#e4e4e7] bg-white p-4 mb-6" data-testid="public-survey-progress">
                    <div className="flex items-center justify-between text-sm">
                        <span className="label-caps">Progresso · {answered.length} / {allMandatory.length} obrigatórias</span>
                        <span className="font-display text-2xl text-[#0d1628]">{progress}%</span>
                    </div>
                    <div className="mt-3 h-1.5 bg-[#e4e4e7]">
                        <div className="h-full bg-[#b89b6f] transition-all duration-500" style={{ width: `${progress}%` }}></div>
                    </div>
                </div>

                {["fib", "whr", "global", "custom"].map((g) => {
                    const items = grouped[g];
                    if (!items?.length) return null;
                    return (
                        <section key={g} className="mb-4" data-testid={`public-section-${g}`}>
                            <button
                                type="button"
                                onClick={() => setOpen({ ...open, [g]: !open[g] })}
                                className="w-full flex items-center justify-between text-left border border-[#e4e4e7] px-5 py-4 bg-[#f5f3ee] hover:bg-[#ece8de] transition-colors"
                                data-testid={`public-section-toggle-${g}`}
                            >
                                <div className="flex items-center gap-3">
                                    {open[g] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                    <span className="label-caps">{GROUP_LABELS[g] || g}</span>
                                    <span className="text-xs text-[#52525b]">({items.length} perguntas)</span>
                                </div>
                                <span className="text-xs text-[#52525b]">
                                    {items.filter((q) => responses[q.id] !== undefined && responses[q.id] !== "").length}/{items.length} respondidas
                                </span>
                            </button>
                            {open[g] && (
                                <div className="border border-t-0 border-[#e4e4e7] divide-y divide-[#e4e4e7] bg-white">
                                    {items.map((q) => (
                                        <PublicQuestionRow
                                            key={q.id}
                                            question={q}
                                            value={responses[q.id]}
                                            indices={data?.indices || []}
                                            onChange={(v) => setVal(q.id, v)}
                                            onToggleMulti={(o) => toggleMulti(q.id, o)}
                                        />
                                    ))}
                                </div>
                            )}
                        </section>
                    );
                })}

                <div className="sticky bottom-0 bg-[#fdfcf9] border-t border-[#e4e4e7] py-4 mt-6 flex justify-end gap-3">
                    <button type="button" onClick={submit} disabled={busy} className="btn-primary" data-testid="public-submit-btn">
                        <Send size={14} /> {busy ? "A submeter..." : "Submeter respostas"}
                    </button>
                </div>

                <p className="text-center text-xs text-[#52525b] mt-6">
                    Powered by IHTP Happiness Development Plan®
                </p>
                <RecaptchaDisclosure className="text-center" />
            </div>
        </div>
    );
}

function PublicQuestionRow({ question: q, value, indices, onChange, onToggleMulti }) {
    const idxs = (q.indices || []).map((id) => indices.find((i) => i.id === id)?.name).filter(Boolean);
    return (
        <div className="p-5" data-testid={`pq-${q.id}`}>
            <div className="flex items-start gap-3 mb-3">
                <span className="text-[#b89b6f] font-display text-lg w-8 flex-shrink-0">{q.order}.</span>
                <div className="flex-1">
                    <div className="text-[#0d1628]">
                        {q.text}
                        {q.mandatory && <span className="text-[#dc2626] ml-1">*</span>}
                    </div>
                    {idxs.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                            {idxs.map((n) => <span key={n} className="text-xs text-[#b89b6f]">{n}</span>)}
                        </div>
                    )}
                </div>
            </div>
            <div className="pl-11">
                {q.type === "scale_1_5" && (
                    <div className="flex gap-2">
                        {[1, 2, 3, 4, 5].map((n) => (
                            <button key={n} type="button"
                                onClick={() => onChange(n)}
                                className={`phase-dot ${value === n ? "active" : ""}`}
                                data-testid={`pscale-${q.id}-${n}`}>{n}</button>
                        ))}
                        {value !== undefined && <CheckCircle2 size={16} className="text-[#10b981] ml-2 mt-2" />}
                    </div>
                )}
                {q.type === "single_choice" && (
                    <div className="space-y-2">
                        {(q.options || []).map((opt) => (
                            <label key={opt} className="flex items-center gap-2 cursor-pointer text-sm">
                                <input type="radio" name={`pq-${q.id}`} checked={value === opt} onChange={() => onChange(opt)} />
                                <span className="text-[#0d1628]">{opt}</span>
                            </label>
                        ))}
                    </div>
                )}
                {q.type === "multi_choice" && (
                    <div className="space-y-2">
                        {(q.options || []).map((opt) => (
                            <label key={opt} className="flex items-center gap-2 cursor-pointer text-sm">
                                <input type="checkbox" checked={Array.isArray(value) && value.includes(opt)}
                                    onChange={() => onToggleMulti(opt)} />
                                <span className="text-[#0d1628]">{opt}</span>
                            </label>
                        ))}
                    </div>
                )}
                {q.type === "number" && (
                    <input className="input-clean max-w-[200px]" type="number" value={value || ""}
                        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} />
                )}
                {q.type === "text" && (
                    <textarea className="input-clean" rows={3} value={value || ""}
                        onChange={(e) => onChange(e.target.value)} />
                )}
            </div>
        </div>
    );
}
